import { SessionNote } from '@bioliberty/lifemind';
import { formatUtcDatesAsLocal } from '../util/date';
import { HttpMethod } from '../util/http';
import { activityLogApi } from './activity-logs';
import { cliniciansApi } from './clinicians';
import { tms } from './tms';

export type CreateSessionNoteParams = {
  sessionId: string;
  text: string;
};

export type UpdateSessionNoteParams = {
  sessionId: string;
  text?: string;
  helpful?: boolean | null;
};

export type GenerateSessionNoteParams = {
  patientId: number;
  sessionId: string;
};

const sessionsApi = tms.injectEndpoints({
  endpoints: (builder) => ({
    updateSessionNote: builder.mutation<void, UpdateSessionNoteParams>({
      query: ({ sessionId, text, helpful }) => ({
        url: `sessions/${sessionId}/note`,
        method: HttpMethod.Patch,
        body: { text, helpful }
      }),
      invalidatesTags: ['PatientSessions'],
      onQueryStarted: async ({ sessionId, helpful }, { dispatch, queryFulfilled, getState }) => {
        // Optimistically update the "helpful" flag of session notes in the clinician dashboard
        const patches = [];

        const cacheEntries = cliniciansApi.util
          .selectInvalidatedBy(getState(), [{ type: 'Clinicians', id: 'me-dashboard' }])
          .filter(({ endpointName }) => endpointName === 'getClinicianDashboard');

        for (const { originalArgs } of cacheEntries) {
          patches.push(
            dispatch(
              cliniciansApi.util.updateQueryData('getClinicianDashboard', originalArgs, (draft) => {
                if (helpful !== undefined) {
                  const session = draft.sessions.find((s) => s.sessionId === sessionId);
                  if (session) {
                    session.noteHelpful = helpful;
                  }
                }
              })
            )
          );
        }

        const patientSessionsCache = activityLogApi.util
          .selectInvalidatedBy(getState(), [{ type: 'PatientSessions' }])
          .filter(({ endpointName }) => endpointName === 'getPatientSessions')?.[0];
        if (patientSessionsCache) {
          patches.push(
            dispatch(
              activityLogApi.util.updateQueryData('getPatientSessions', patientSessionsCache.originalArgs, (draft) => {
                if (helpful !== undefined) {
                  const session = draft.items.find((s) => s.sessionId === sessionId);
                  if (session) {
                    session.noteHelpful = helpful;
                  }
                }
              })
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      }
    }),
    generateSessionNote: builder.mutation<SessionNote, GenerateSessionNoteParams>({
      query: ({ patientId, sessionId }) => ({
        url: `patients/${patientId}/sessions/${sessionId}/note/generate`,
        method: HttpMethod.Put
      }),
      invalidatesTags: ['Clinicians', 'PatientSessions'],
      onQueryStarted: async ({ sessionId }, { dispatch, queryFulfilled, getState }) => {
        const { data } = await queryFulfilled;

        const cacheEntries = cliniciansApi.util
          .selectInvalidatedBy(getState(), [{ type: 'Clinicians', id: 'me-dashboard' }])
          .filter(({ endpointName }) => endpointName === 'getClinicianDashboard');

        for (const { originalArgs } of cacheEntries) {
          dispatch(
            cliniciansApi.util.updateQueryData('getClinicianDashboard', originalArgs, (draft) => {
              const session = draft.sessions.find((s) => s.sessionId === sessionId);
              if (session) {
                session.note = formatUtcDatesAsLocal(data.text);
                session.noteHelpful = null;
              }
            })
          );
        }
      }
    })
  })
});

export const { useUpdateSessionNoteMutation, useGenerateSessionNoteMutation } = sessionsApi;
